import React, { useMemo, useState, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import SingleItemDetailTemplate from '../../../Reusable_Components/SingleItemDetailTemplate';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import useDetailFetch from '../../../../hooks/useDetailFetch';

const SingleUserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useContext(ToastContext);

  // atob() throws on malformed input, and a stale/garbage id (e.g. the
  // literal "undefined" that used to get base64-encoded before the list's
  // `id` field was fixed) decodes to something with no digits at all —
  // guard both so we can show a clear message instead of silently never
  // firing the fetch below.
  let decodedId = '';
  try {
    decodedId = userId ? atob(userId) : '';
  } catch {
    decodedId = '';
  }
  const id = decodedId.replace(/\D/g, '');
  const [showDelete, setShowDelete] = useState(false);

  const url = id ? `${API_DOMAIN}${API_ROUTES.securityUsers}${id}/` : null;
  const { data: raw, loading } = useDetailFetch(url);

  // Re-key onto plain, template-friendly field names — the API's `tenant_id`
  // would otherwise be silently dropped by SingleItemDetailTemplate's
  // hard-coded EXCLUDE_FIELDS list.
  const details = useMemo(() => {
    if (!raw) return null;
    return {
      username: raw.username,
      email: raw.email,
      first_name: raw.first_name,
      last_name: raw.last_name,
      role: raw.role,
      company_name: raw.company_name,
      tenant: raw.tenant_id,
      is_active: raw.is_active,
      date_joined: raw.date_joined,
    };
  }, [raw]);

  const columns = [
    { label: 'Username', field: 'username' },
    { label: 'Email', field: 'email' },
    { label: 'First Name', field: 'first_name' },
    { label: 'Last Name', field: 'last_name' },
    { label: 'Role', field: 'role' },
    { label: 'Company', field: 'company_name' },
    { label: 'Tenant', field: 'tenant' },
    { label: 'Active', field: 'is_active' },
    { label: 'Date Joined', field: 'date_joined' },
  ];

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_DOMAIN}${API_ROUTES.securityUsers}${id}/`, { headers });
      showToast('User deleted successfully', 'success');
      navigate('/administrator/user/list');
    } catch (error) {
      console.error('Error deleting user:', error);
      showToast('Error deleting user', 'error');
    }
  };

  if (!id) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ fontSize: 14, color: 'var(--text-sub)', marginBottom: 16 }}>
            This user link is invalid or has expired. Go back to the list and try again.
          </p>
          <button className="btn btn-secondary" onClick={() => navigate('/administrator/user/list')}>
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SingleItemDetailTemplate
        details={details}
        columns={columns}
        loading={loading}
        entityName="User"
        listPath="/administrator/user/list"
        onDelete={() => setShowDelete(true)}
      />
      <ConfirmModal
        open={showDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmLabel="Delete"
        confirmDanger
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

export default SingleUserDetails;
